import type { ResolvedCollection } from '@rstore/shared';
import type { AnyItem } from '#layers/data-model';

interface FieldWithSerialize {
	serialize?: (value: unknown) => unknown;
}

/**
 * Prepare a mutation payload for the engine by walking its nested relation operations once
 * and, at every level, doing two things to each item:
 *
 * 1. Drop the foreign-key fields of constrained relations (the engine derives them from the
 *    relation operation, so sending them is redundant and can conflict).
 * 2. Serialize primitive field values with each collection's field parsers — a nested write
 *    (e.g. editing an M2M target's JSON `tags` field) would otherwise send a raw value
 *    (a `List<string>`, a Temporal date object) that the engine rejects.
 *
 * rstore serializes only the top-level item, so its own primitive fields are left untouched
 * (`serializeSelf: false`); its constrained foreign keys are still dropped. Both concerns
 * share the same recursion, so they run in a single pass rather than two walks.
 */
export function prepareRelationFields(
	item: AnyItem,
	collection: ResolvedCollection,
	allCollections: ResolvedCollection[],
): AnyItem {
	return prepareItem(item, collection, allCollections, false);
}

export function findTargetCollectionFromNormalized(
	allCollections: ResolvedCollection[],
	relation: { to: Array<{ collection: string }> },
): ResolvedCollection | undefined {
	for (const target of relation.to) {
		const found = allCollections.find((c) => c.name === target.collection);
		if (found) return found;
	}
}

/**
 * Drop constrained FK fields and recurse into relation operations. When `serializeSelf` is
 * true (every level except the rstore-serialized top), also serialize the item's own
 * primitive fields. FK fields are dropped before serializing, so serialization skips them.
 */
function prepareItem(
	item: AnyItem,
	collection: ResolvedCollection,
	allCollections: ResolvedCollection[],
	serializeSelf: boolean,
): AnyItem {
	let result = item;
	const constrainedRelations = collection.meta?.relationsWithConstraint;

	for (const relationKey in collection.normalizedRelations) {
		const relation = collection.normalizedRelations[relationKey]!;

		if (constrainedRelations?.has(relationKey)) {
			for (const target of relation.to) {
				for (const k in target.on) {
					result = { ...result, [target.on[k]!]: undefined };
				}
			}
		}

		const value = result[relationKey];
		if (value == null) continue;

		const targetCollection = findTargetCollectionFromNormalized(allCollections, relation);
		if (!targetCollection) continue;

		result = { ...result, [relationKey]: prepareRelationOps(value, targetCollection, allCollections) };
	}

	return serializeSelf ? serializeCollectionFields(result, collection) : result;
}

/**
 * Recurse into relation operation values (`_create.data` / `_update.data`), preparing the
 * nested target item data. Other operations (`_connect`, `_disconnect`, `_delete`) and
 * non-object values pass through unchanged.
 */
export function prepareRelationOps(
	value: unknown,
	targetCollection: ResolvedCollection,
	allCollections: ResolvedCollection[],
): unknown {
	if (!value || typeof value !== 'object') return value;

	if (Array.isArray(value)) {
		return value.map((op) => prepareRelationOps(op, targetCollection, allCollections));
	}

	const obj = value as Record<string, unknown>;
	let result = obj;

	for (const opKey of ['_create', '_update'] as const) {
		const op = obj[opKey];
		if (!op || typeof op !== 'object') continue;

		const data = (op as { data?: unknown }).data;

		if (Array.isArray(data)) {
			result = { ...result, [opKey]: { ...op, data: data.map((entry: AnyItem) => prepareItem(entry, targetCollection, allCollections, true)) } };
		}
		else if (data && typeof data === 'object') {
			result = { ...result, [opKey]: { ...op, data: prepareItem(data as AnyItem, targetCollection, allCollections, true) } };
		}
	}

	return result;
}

/**
 * Apply each field's `serialize` parser to the matching value on the item.
 */
function serializeCollectionFields(item: AnyItem, collection: ResolvedCollection): AnyItem {
	const fields = collection.fields as Record<string, FieldWithSerialize> | undefined;
	if (!fields) return item;

	let result = item;

	for (const path in fields) {
		const serialize = fields[path]?.serialize;
		if (!serialize) continue;

		const value = result[path];
		if (value == null) continue;

		result = { ...result, [path]: serialize(value) };
	}

	return result;
}
